import { Button } from '@/components/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/dropdown';
import { FormLabel } from '@/components/form/formlabel';
import { Input } from '@/components/form/input';
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/form/select';
import { Modal } from '@/components/modal';
import { DataTable } from '@/components/table/datatable';
import { clearErrors } from '@/reducers/auth';
import { AppDispatch } from '@/root/store';
import {
  GetBanks,
  StoreBankAccount,
  UpdateBankAccount,
} from '@/services/financial';
import { NoSymbolIcon, PencilSquareIcon } from '@heroicons/react/24/outline';
import {
  DocumentCheckIcon,
  EllipsisHorizontalIcon,
  PlusIcon,
} from '@heroicons/react/24/solid';
import { ColumnDef } from '@tanstack/react-table';
import moment from 'moment';
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useParams } from 'react-router-dom';

interface BankAccount {
  id: string;
  account_name: string;
  account_number: string;
  bank: {
    id: string;
    code: string;
    name: string;
  };
  created_at: string;
}

export function OrgCommerceBanks() {
  const location = useLocation();
  const [addBankModal, setAddBankModal] = useState({
    state: 'create',
    status: false,
  });
  const dispatch: AppDispatch = useDispatch();
  const [page, setPage] = useState(1);
  const { slug } = useParams();

  const { bankAccounts, banks, paginator, loading, errors } = useSelector(
    (state: any) => state.financial
  );

  const { event } = useSelector((state: any) => state.event);

  const [formData, setFormData] = useState({
    bank_account_id: '',
    bank_id: '',
    account_name: '',
    account_number: '',
  });

  const setData = (value: string, element: string) => {
    setFormData({ ...formData, [element]: value });

    dispatch(clearErrors());
  };

  const submit = () => {
    if (addBankModal.state === 'create') {
      dispatch(StoreBankAccount(event.id, formData));
    } else {
      dispatch(UpdateBankAccount(event.id, formData.bank_account_id, formData));
    }
  };

  const queryParams = new URLSearchParams(location.search);
  const searchQuery: string = queryParams.get('search') ?? '';

  useEffect(() => {
    dispatch(GetBanks(slug));
  }, [slug, searchQuery, page]);

  useEffect(() => {
    setAddBankModal({ ...addBankModal, status: false });
  }, [bankAccounts]);

  const columns: ColumnDef<BankAccount>[] = [
    {
      header: 'Pemilik Rekening',
      accessorKey: 'account_name',
    },
    {
      header: 'No. Rekening',
      accessorKey: 'account_number',
    },
    {
      header: 'Nama Bank',
      accessorKey: 'bank_name',
      cell: ({ row }) => {
        const bankCode = row.original?.bank?.name;
        return <span>{bankCode}</span>;
      },
    },
    {
      header: 'Ditambahkan pada',
      accessorKey: 'created_at',
      cell: ({ row }) => {
        const createdAt = row.original?.created_at;
        return <span>{moment(createdAt).format('lll')}</span>;
      },
    },
    {
      header: 'Aksi',
      cell: ({ row }) => {
        return (
          <DropdownMenu>
            <DropdownMenuTrigger>
              <Button
                size='sm'
                variant='neutral'
                leftIcon={<EllipsisHorizontalIcon />}
              />
            </DropdownMenuTrigger>
            <DropdownMenuContent>
              <DropdownMenuItem
                onClick={() => {
                  setAddBankModal({
                    state: 'edit',
                    status: true,
                  });
                  setFormData({
                    account_name: row.original.account_name,
                    account_number: row.original.account_number,
                    bank_id: row.original.bank.id,
                    bank_account_id: row.original.id,
                  });
                }}
              >
                <PencilSquareIcon className='mr-2 h-4 w-4' />
                <span>Ubah</span>
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        );
      },
    },
  ];

  return (
    <>
      <Modal
        show={addBankModal.status}
        onRequestClose={() =>
          setAddBankModal({ ...addBankModal, status: false })
        }
        width='medium'
        title={
          (addBankModal.state === 'create' ? 'Tambah' : 'Edit') + ' rekening'
        }
        body={
          <div className='flex flex-col gap-4'>
            <FormLabel label='Pilih bank' errorText={errors.bank_id} required>
              <Select
                onValueChange={(e) => {
                  setData(e, 'bank_id');
                }}
              >
                <SelectTrigger>
                  <SelectValue defaultValue={formData.bank_id} />
                </SelectTrigger>
                <SelectContent>
                  <SelectGroup>
                    {banks.map((item: any, key: string) => (
                      <SelectItem key={key} value={item.id}>
                        {item.name}
                      </SelectItem>
                    ))}
                  </SelectGroup>
                </SelectContent>
              </Select>
            </FormLabel>
            <FormLabel
              label='No. rekening'
              errorText={errors.account_number}
              required
            >
              <Input
                type='text'
                onChange={(e) => setData(e.target.value, 'account_number')}
                value={formData.account_number}
                required
              />
            </FormLabel>

            <FormLabel
              label='Nama pemilik rekening'
              errorText={errors.account_name}
              helperText={
                'Pastikan nama pemilik rekening sesuai dengan identitas penanggung jawab event.'
              }
              required
            >
              <Input
                type='text'
                value={formData.account_name}
                onChange={(e) => setData(e.target.value, 'account_name')}
                required
              />
            </FormLabel>
          </div>
        }
        footer={
          <div className='flex justify-end gap-3'>
            <Button
              variant='neutral'
              onClick={() => setAddBankModal({ ...addBankModal, status: true })}
              leftIcon={<NoSymbolIcon />}
            >
              Batal
            </Button>
            <Button
              loading={loading}
              onClick={submit}
              leftIcon={<DocumentCheckIcon />}
            >
              Simpan
            </Button>
          </div>
        }
      />

      <DataTable
        name='Rekening Bank'
        noun='rekening'
        ctaButton={
          bankAccounts.length === 0 ? (
            <Button
              onClick={() => {
                setAddBankModal({ state: 'create', status: true });
              }}
              leftIcon={<PlusIcon />}
            >
              Tambah Rekening
            </Button>
          ) : (
            <></>
          )
        }
        columns={columns}
        data={bankAccounts}
        total={paginator.to}
        currentTotal={paginator.total}
        currentPage={paginator.current_page}
        pages={paginator.last_page}
        onPageRequest={(nextPage) => setPage(nextPage)}
      />
    </>
  );
}
